import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { RenderedDocument } from '@oscom/shared';
import { getCustomer } from '../api/customers';
import { documentImageUrl, renderDocument } from '../api/documents';
import { useSession } from '../auth/SessionProvider';
import { CustomerContextNav } from '../components/CustomerContextNav';
import { DocumentPreview } from '../components/DocumentPreview';
import { Layout } from '../components/Layout';
import { NotFoundPage } from './NotFoundPage';

/**
 * Reading view of one document; the browser's print dialog turns it into a
 * PDF until the real export (phase 5). Readers only ever see this view.
 */
export function DocumentPreviewPage(): ReactNode {
  const { session } = useSession();
  const { customerId: customerIdParam, documentId: documentIdParam } = useParams<{
    customerId: string;
    documentId: string;
  }>();
  const enabled = session?.status === 'authenticated' && session.me.features?.documents === true;
  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [rendered, setRendered] = useState<RenderedDocument | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!customerIdParam || !documentIdParam || !enabled) return;
    let cancelled = false;
    Promise.all([getCustomer(customerIdParam), renderDocument(customerIdParam, documentIdParam)])
      .then(([customer, result]) => {
        if (cancelled) return;
        setCustomerName(customer.name);
        setRendered(result);
      })
      .catch((caught: unknown) => {
        if (!cancelled)
          setLoadError(
            caught instanceof Error ? caught.message : 'Das Dokument konnte nicht geladen werden.',
          );
      });
    return () => {
      cancelled = true;
    };
  }, [customerIdParam, documentIdParam, enabled]);

  if (session?.status !== 'authenticated') return null;
  if (!enabled) return <NotFoundPage />;
  if (!customerIdParam || !documentIdParam) return null;
  const { me } = session;
  const customerId = customerIdParam;
  const documentId = documentIdParam;
  const base = `/kunden/${encodeURIComponent(customerId)}/dokumente`;

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        {customerName ? (
          <div className="no-print">
            <CustomerContextNav
              customerId={customerId}
              customerName={customerName}
              features={me.features}
            />
          </div>
        ) : null}
        <div className="doc-preview__actions no-print">
          <button type="button" className="button button--primary" onClick={() => window.print()}>
            Drucken / als PDF speichern
          </button>
          {me.permissions.includes('documents:write') ? (
            <Link className="button" to={`${base}/${encodeURIComponent(documentId)}`}>
              Zurück zum Editor
            </Link>
          ) : null}
          <Link className="button button--ghost" to={base}>
            Zur Dokumentenliste
          </Link>
        </div>
        {loadError ? (
          <p className="notice notice--warning">{loadError}</p>
        ) : !rendered ? (
          <p className="muted" role="status" aria-live="polite">
            Dokument wird aufgebaut ...
          </p>
        ) : (
          <div className="card">
            <DocumentPreview
              customerId={customerId}
              rendered={rendered}
              imageUrl={(assetId) => documentImageUrl(customerId, documentId, assetId)}
            />
          </div>
        )}
      </section>
    </Layout>
  );
}
